// Pinia store du chat assistant (P7.H, ADR 0007).
// Gere : drawer ouvert/ferme (Cmd+J), conversations sidebar, messages
// du thread courant, streaming SSE token-by-token, plan en attente
// d'approbation, raccourcis clavier.

import { defineStore } from "pinia";
import * as api from "@/shared/api/assistant";
import type {
  AssistantConversation,
  AssistantMessage,
  ChatStreamEvent,
  PlanJson,
} from "@/shared/api/assistant";

interface PendingMessage {
  role: "assistant";
  content: string;
  streaming: boolean;
}

interface ChatState {
  open: boolean;
  conversations: AssistantConversation[];
  currentConversationId: string | null;
  messages: AssistantMessage[];
  pending: PendingMessage | null;
  pendingPlan: { plan_id: string; plan_json: PlanJson } | null;
  thinking: boolean;
  error: string | null;
  loadingConversation: boolean;
}

export const useChatStore = defineStore("assistant-chat", {
  state: (): ChatState => ({
    open: false,
    conversations: [],
    currentConversationId: null,
    messages: [],
    pending: null,
    pendingPlan: null,
    thinking: false,
    error: null,
    loadingConversation: false,
  }),

  actions: {
    toggleDrawer(): void {
      this.open = !this.open;
      if (this.open && this.conversations.length === 0) {
        void this.loadConversations();
      }
    },

    closeDrawer(): void {
      this.open = false;
    },

    async loadConversations(): Promise<void> {
      try {
        this.conversations = await api.listConversations();
      } catch (err) {
        this.error = err instanceof Error ? err.message : "load conversations failed";
      }
    },

    async openConversation(id: string): Promise<void> {
      this.loadingConversation = true;
      this.error = null;
      this.currentConversationId = id;
      try {
        this.messages = await api.listMessages(id);
        this.pendingPlan = null;
      } catch (err) {
        this.error = err instanceof Error ? err.message : "load messages failed";
      } finally {
        this.loadingConversation = false;
      }
    },

    newConversation(): void {
      this.currentConversationId = null;
      this.messages = [];
      this.pendingPlan = null;
      this.error = null;
    },

    pushUserMessage(content: string): void {
      this.messages.push({
        id: `local-${crypto.randomUUID()}`,
        conversation_id: this.currentConversationId ?? "",
        role: "user",
        content,
        tool_calls: null,
        tool_results: null,
        tokens_used: null,
        model_version: null,
        created_at: new Date().toISOString(),
      });
    },

    async send(userMessage: string): Promise<void> {
      if (this.thinking) return;
      const trimmed = userMessage.trim();
      if (!trimmed) return;
      this.error = null;
      this.thinking = true;
      this.pendingPlan = null;
      this.pushUserMessage(trimmed);
      this.pending = { role: "assistant", content: "", streaming: true };
      try {
        for await (const ev of api.chatStream(trimmed, this.currentConversationId ?? undefined)) {
          this.handleEvent(ev);
          if (ev.type === "error" || ev.type === "done") return;
        }
      } catch (err) {
        this.error = err instanceof Error ? err.message : "chat stream failed";
      } finally {
        this.thinking = false;
        if (this.pending) this.pending.streaming = false;
      }
    },

    finalizeAssistantMessage(messageId: string, conversationId: string): void {
      this.currentConversationId = conversationId;
      this.messages.push({
        id: messageId,
        conversation_id: conversationId,
        role: "assistant",
        content: this.pending?.content ?? "",
        tool_calls: null,
        tool_results: null,
        tokens_used: null,
        model_version: null,
        created_at: new Date().toISOString(),
      });
      this.pending = null;
      void this.loadConversations();
    },

    handleEvent(ev: ChatStreamEvent): void {
      if (ev.type === "token" && this.pending) {
        this.pending.content += ev.data.delta;
        return;
      }
      if (ev.type === "plan") {
        this.pendingPlan = ev.data;
        return;
      }
      if (ev.type === "error") {
        this.error = `${ev.data.code} : ${ev.data.message}`;
        this.pending = null;
        return;
      }
      if (ev.type === "done") {
        this.finalizeAssistantMessage(ev.data.message_id, ev.data.conversation_id);
      }
    },

    async approveAndExecute(accountId: string): Promise<void> {
      if (!this.pendingPlan) return;
      try {
        await api.approvePlan(this.pendingPlan.plan_id, accountId);
        this.pendingPlan = null;
        if (this.currentConversationId) {
          this.messages = await api.listMessages(this.currentConversationId);
        }
      } catch (err) {
        this.error = err instanceof Error ? err.message : "approve failed";
      }
    },

    async rejectPlan(): Promise<void> {
      if (!this.pendingPlan) return;
      try {
        await api.rejectPlan(this.pendingPlan.plan_id);
        this.pendingPlan = null;
      } catch (err) {
        this.error = err instanceof Error ? err.message : "reject failed";
      }
    },
  },
});
